/**
 * Blocking checks for a GTI order.
 *
 * PD-1529 — the chart data that reaches this form is auto-populated and was only
 * ever warned about, so an order could be submitted that GTI cannot process: a
 * patient with no coverage still produced a requisition with no payer to bill.
 * beHuman only accepts insured patients and bills nothing but their insurance,
 * so coverage is a hard requirement rather than one billing option among three.
 *
 * The rules below are GTI's own stated requirements — "as long as they specify
 * that they're for GTI labs, have the tests ordered, ICD codes, provider
 * signature, and then the patient address, insurance, and contact info, we are
 * good to go" — plus a date of birth, which the lab matches a specimen on.
 *
 * Deliberately NOT blocking, because they are the lab's stated behaviour rather
 * than a defect in the order: selecting both CMP and BMP (GTI processes only
 * CMP) and a "Fast Required" panel without the fasting instruction. Both surface
 * as warnings on the form.
 */
export interface GtiOrderValidationInput {
  /** Whether the chart resolved an active primary coverage. */
  hasInsuranceOnFile: boolean
  /** Whether the coverage lookup has finished. Until it has, `hasInsuranceOnFile`
   *  says nothing — so the order waits rather than assuming the patient is
   *  insured. */
  insuranceResolved: boolean
  diagnosisText: string
  selectedTests: string[]
  signatureDataUrl: string
  patient: {
    dateOfBirth: string
    address: string
    phone: string
    email: string
  }
}

/** Every reason this order cannot be sent, so the form can list them at once. */
export function validateGtiOrder(input: GtiOrderValidationInput): string[] {
  const problems: string[] = []

  if (!input.insuranceResolved) {
    problems.push(
      "Still loading this patient's insurance. GTI orders are billed to insurance, " +
        'so wait for it to load before ordering.',
    )
  } else if (!input.hasInsuranceOnFile) {
    problems.push(
      'This patient has no active insurance on file. GTI orders are billed to insurance, ' +
        "so add the patient's coverage before ordering.",
    )
  }

  if (!input.diagnosisText.trim()) {
    problems.push(
      'Add at least one ICD-10 diagnosis code. GTI requires a diagnosis to process and bill the order.',
    )
  }

  if (input.selectedTests.length === 0) {
    problems.push('Select at least one test to order.')
  }

  if (!input.signatureDataUrl) {
    problems.push('Sign the order. GTI requires the ordering provider signature.')
  }

  if (!input.patient.address.trim()) {
    problems.push(
      "Add the patient's address to their chart. GTI's collector needs it to draw the specimen.",
    )
  }

  if (!input.patient.phone.trim() && !input.patient.email.trim()) {
    problems.push(
      "Add a phone number or email to the patient's chart. " +
        'GTI needs a way to reach them to arrange the draw.',
    )
  }

  if (!input.patient.dateOfBirth.trim()) {
    problems.push("Add the patient's date of birth to their chart. GTI matches the specimen on it.")
  }

  return problems
}
